import { useState, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import qs from 'qs';
import { useSearchParams } from 'react-router-dom';

import { API_URL, get } from 'lib/network/api';
import { AgeDefault, TypeClassDefault } from 'lib/DefaultData';

import FullLayout from 'layout/FullLayout';
import InputSelected from 'components/InputSelected';
import AutocompleteDropdown from 'components/AutocompleteDropdown';
import CardOurClass from 'components/CardOurClass';
import ModalViewClass from './ComponentsPage/ModalViewClass';

const OurClass = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentParams = qs.parse(searchParams.toString(), { comma: true });

  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);

  useEffect(() => {
    const params = { ...currentParams };
    if (!params.page) params.page = 1;
    if (!params.limit) params.limit = 6;
    setSearchParams(params);
  }, []);

  const updateParam = (key, value) => {
    const updated = { ...currentParams };
    if (!value) delete updated[key];
    else updated[key] = value;

    if (key !== 'page') updated.page = 1;

    setSearchParams(updated);
  };

  const fetchListClass = async ({ pageParam = 1 }) => {
    const params = {
      page: pageParam,
      limit: currentParams.limit || 6,
      teacher: currentParams.teacher_id || null,
      class_type: currentParams.class_type || null,
      age_group: currentParams.age_group || null,
    };

    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );

    return get(
      `${API_URL}/api/event/class/no-login?${qs.stringify(filteredParams, {
        arrayFormat: 'repeat',
      })}`,
    );
  };

  const {
    data: classData,
    isLoading: isLoadingClass,
    hasNextPage: hasMoreClass,
    fetchNextPage: fetchNextClassPage,
    isFetchingNextPage: isFetchingNextClass,
    isError: isErrorClass,
  } = useInfiniteQuery({
    queryKey: ['fetchListClass', currentParams],
    queryFn: fetchListClass,
    getNextPageParam: (lastPage, allPages) => {
      const items = lastPage?.data || [];
      return items.length === Number(currentParams.limit || 6) ? allPages.length + 1 : undefined;
    },
  });

  const classList = classData ? classData.pages.flatMap((page) => page?.data || []) : [];

  // Filter classList to only show classes with next_class_date
  const filteredClassList = classList.filter((item) => item.next_class_date);

  const fetchListTeacher = async ({ pageParam = 1 }) => {
    const params = { page: pageParam, limit: 10 };
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
    return get(
      `${API_URL}/api/teachers/no-login?${qs.stringify(filteredParams, {
        arrayFormat: 'repeat',
      })}`,
    );
  };

  const {
    data: teacherData,
    hasNextPage: hasMoreTeachers,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['fetchListTeacher'],
    queryFn: fetchListTeacher,
    getNextPageParam: (lastPage, allPages) => {
      const teachers = lastPage?.data?.teachers || [];
      return teachers.length === 10 ? allPages.length + 1 : undefined;
    },
  });

  const teacherOptions = teacherData
    ? teacherData.pages.flatMap((page) =>
        (page?.data?.teachers || []).map((item) => ({
          id: item.id,
          name: item.name,
          value: item.id,
        })),
      )
    : [];

  const handleLoadMoreTeachers = () => {
    if (hasMoreTeachers && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    updateParam('teacher_id', teacher?.value || '');
  };

  const onPickClass = (item) => {
    setSelectedClass(item);
    setOpenModal(true);
  };

  return (
    <FullLayout>
      <div className="container mx-auto xl:pt-2 xl:pb-6 xl:px-0 pt-2 pb-2 px-2 sm:px-4 lg:px-8">
        <div className="w-full md:w-max">
          <h2 className="text-2xl text-kin-text font-semibold mb-2">Our Classes</h2>

          {/* ✅ Filter */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 items-center">
            <InputSelected
              dataOption={TypeClassDefault}
              placeholder="Type Class"
              selected={TypeClassDefault.find((i) => i.value === currentParams.class_type) || null}
              setSelected={(opt) => updateParam('class_type', opt?.value || '')}
              showClearOption
              onClear={() => updateParam('class_type', '')}
              width={'md:w-[200px] w-full'}
            />

            <InputSelected
              dataOption={AgeDefault.map((i) => ({
                name: `${i.name} ${i.description}`,
                value: i.value,
              }))}
              placeholder="Age Group"
              selected={AgeDefault.find((i) => i.value === currentParams.age_group) || null}
              setSelected={(opt) => updateParam('age_group', opt?.value || '')}
              showClearOption
              onClear={() => updateParam('age_group', '')}
              width={'md:w-[200px] w-full'}
            />

            <AutocompleteDropdown
              options={teacherOptions}
              selected={selectedTeacher}
              setSelected={handleTeacherSelect}
              onLoadMore={handleLoadMoreTeachers}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMoreTeachers}
              placeholder="Search Teachers..."
              onClear={() => {
                setSelectedTeacher(null);
                updateParam('teacher_id', '');
              }}
              width={'md:w-[200px] w-full'}
            />
          </div>
        </div>

        {/* ✅ Loading */}
        {isLoadingClass && <p className="text-center mt-6">Loading...</p>}

        {/* ✅ Error */}
        {isErrorClass && <p className="text-center mt-6 text-red-500">Something Went Wrong.</p>}

        {!isLoadingClass && filteredClassList.length === 0 && (
          <p className="text-center mt-6 text-gray-500">No Classes Available.</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mt-6 pb-20 gap-4">
          {filteredClassList.map((items, idx) => (
            <CardOurClass key={idx} item={items} onPickClass={onPickClass} />
          ))}
        </div>

        {hasMoreClass && !isLoadingClass && (
          <div className="flex justify-center mt-3">
            <button
              className="px-4 py-2 bg-primary-main text-white rounded shadow w-full max-w-xs"
              onClick={() => fetchNextClassPage()}
              disabled={isFetchingNextClass}>
              {isFetchingNextClass ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}

        {openModal && (
          <ModalViewClass item={selectedClass} openModal={openModal} setOpenModal={setOpenModal} />
        )}
      </div>
    </FullLayout>
  );
};

export default OurClass;
